import { Button, Image, Table } from "antd";
import { ColumnsType } from "antd/es/table";
import { ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { useAppSelector } from "../../../app/store";
import AddIcon from "../../../assets/icons/AddIcon";
import EditIcon from "../../../assets/icons/EditIcon";
import ViewIcon from "../../../assets/icons/ViewIcon";
import PDF_IMG from "../../../assets/images/pdf.svg";
import LinkIcon from "../../../assets/web/icons/LinkIcon";
import ConfirmPasswordResetModal from "../../../components/Businesses/ConfirmPasswordResetModal";
import EditBusinessInfoModal from "../../../components/Businesses/EditBusinessInfoModal";
import PinNumberModal from "../../../components/Businesses/PinNumberModal";
import CardTitle from "../../../components/common/CardTitle";
import CustomCard from "../../../components/common/CustomCard";
import Loader from "../../../components/ui/Loader";
import { useGetSingleCompanyQuery } from "../../../features/portal/companyOnboard/companyOnboardApi";
import { useBalanceCheckQuery } from "../../../features/portal/vendorPayment/vendorPaymentApi";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { formatDate } from "../../../lib/time";
import { GetSingleCompanyDataType } from "../../../types/portal/companyOnboard";
import { addCommaInNumber } from "../../../utils/number";
import { capitalize, getExtension } from "../../../utils/string";
import { addKeyInArray } from "../../../utils/utils";

interface LogItem {
  time: string;
  date: string;
  message: string;
  user: string;
  role: string;
  status: "success" | "warning" | "error";
}

const logs: LogItem[] = [
  {
    time: "06:01 PM",
    date: "06 Nov, 2024",
    message: "This Business Activated Successfully!",
    user: "Md Abdul Hamid",
    role: "System Admin",
    status: "success",
  },
  {
    time: "06:01 PM",
    date: "06 Nov, 2024",
    message: "Request to Active This Business",
    user: "Md Abdul Hamid",
    role: "Finance Admin",
    status: "warning",
  },
  {
    time: "06:01 PM",
    date: "06 Nov, 2024",
    message: "This Business Inactivated Successfully!",
    user: "Md Abdul Hamid",
    role: "System Admin",
    status: "error",
  },
  {
    time: "06:01 PM",
    date: "06 Nov, 2024",
    message: "Request to Inactive This Business",
    user: "Md Abdul Hamid",
    role: "Business Team",
    status: "warning",
  },
];

const statusColors = {
  success: "bg-green-500",
  warning: "bg-yellow-400",
  error: "bg-red-500",
};

const columns = [
  { title: "Name", dataIndex: "name" },
  { title: "Issue Date", dataIndex: "issueDate" },
  { title: "Expire Date", dataIndex: "expireDate" },
  {
    title: "Action",
    dataIndex: "file",
    width: 100,
    render: (file?: string | null) => {
      return file ? (
        <div className="inline-flex items-end gap-2">
          <Image
            src={getExtension(file) === "pdf" ? PDF_IMG : file}
            height={40}
          />
          <a
            href={file}
            target="_blank"
            className="text-blue-500"
            rel="noreferrer"
          >
            <LinkIcon color="currentColor" />
          </a>
        </div>
      ) : (
        "N/A"
      );
    },
  },
];

const backLink = "/admin/businesses/business-list";

export default function BusinessListDetails() {
  const { id } = useParams();
  const { role } = useAppSelector((state) => state.auth);
  const { open, handleOpen, handleClose } = useModal<
    any,
    "CONFIRM" | "SET_PIN"
  >();
  const { isError, error, isLoading, data } = useGetSingleCompanyQuery(
    id || "",
    {
      skip: !id,
    }
  );

  const {
    isError: isBalanceCheckError,
    error: balanceCheckError,
    data: balanceCheck,
  } = useBalanceCheckQuery(
    {
      walletNumber: data?.data?.wallet_number || "",
      companyId: Number(id),
    },
    {
      skip: !data?.data?.wallet_number || !id,
    }
  );
  useError(isError, error, "Get single company fail");
  useError(isBalanceCheckError, balanceCheckError, "Get balance data fail");
  const detailsData = data?.data;

  const documentList = [
    {
      id: 1,
      name: "TIN Certificate",
      issueDate: detailsData?.tin_certificate_issue_date
        ? formatDate(detailsData?.tin_certificate_issue_date)
        : "N/A",
      expireDate: detailsData?.tin_certificate_expire_date
        ? formatDate(detailsData?.tin_certificate_expire_date)
        : "N/A",
      file: detailsData?.tin_certificate_file_link,
    },
    // {
    //   id: 2,
    //   name: "Trade License",
    //   expireDate: formatDate(detailsData?.tin_certificate_issue_date),
    //   file: null,
    // },
  ];

  const columnAssignProduct: ColumnsType<GetSingleCompanyDataType> = [
    {
      title: "Product ID",
      dataIndex: "project_id",
      align: "center",
      render: (val: any) => <div className="text-center">{val || "N/A"}</div>,
    },
    {
      title: "Product Name",
      dataIndex: "project",
      align: "center",
      render: (val: any) => (
        <div className="text-center">{val ? val?.name : "N/A"}</div>
      ),
    },
    {
      title: "Logo",
      dataIndex: "project",
      align: "center",
      render: (val: any) => (
        <div className="flex justify-center">
          <img
            src={val?.logo}
            alt="product_logo"
            className="w-10 h-10 object-contain rounded"
          />
        </div>
      ),
    },
    {
      title: "Action",
      width: 100,
      align: "center",
      render: (record) => (
        <div className="flex justify-center">
          <Link to={`/admin/products/product-list`}>
            <Button className="action-button" icon={<ViewIcon />} />
          </Link>
        </div>
      ),
    },
  ];

  const columnUserCreation = [
    {
      title: "User Name",
      dataIndex: "username",
      render: (val: string) => capitalize(val || ""),
    },
    { title: "Email", dataIndex: "email" },
    {
      title: "Role",
      dataIndex: "role",
      render: (val: any) =>
        val ? val.charAt(0).toUpperCase() + val.slice(1) : "",
    },
    {
      title: "Action",
      dataIndex: "id",
      width: 100,
      render: (val: string) => (
        <Link to={`/admin/businesses/business-list/${id}/details/${val}`}>
          <Button className="action-button" icon={<ViewIcon />} />
        </Link>
      ),
    },
  ];

  return (
    <SidebarLayout title="Business Details " backLink={backLink}>
      {isLoading ? (
        <Loader />
      ) : (
        <>
          {open.type === "CONFIRM" && (
            <ConfirmPasswordResetModal
              open={open.open}
              // data={open.data}
              handleClose={handleClose}
            />
          )}
          {open.type === "SET_PIN" && (
            <PinNumberModal
              open={open.open}
              data={open.data}
              handleClose={handleClose}
            />
          )}
          {open.type === "UPDATE" && (
            <EditBusinessInfoModal
              open={open.open}
              type={open.type}
              // data={open.data}
              handleClose={handleClose}
            />
          )}

          <CustomCard className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              <div className="flex items-center justify-between">
                <h5 className="text-lg font-semibold text-header">
                  Business Info
                </h5>
                {role === "Admin" ? (
                  <Link to={`${backLink}/update/${id}`}>
                    <Button
                      className="!border-[#4A72FF] !text-[#4A72FF]"
                      icon={<EditIcon />}
                    >
                      Edit Info
                    </Button>
                  </Link>
                ) : null}
              </div>
              <div className="flex items-center justify-between">
                {/* <img
                  src={BusinessInfoLogo}
                  alt="Business Info Logo"
                  className="w-20 h-20 rounded-full"
                /> */}
                <div />

                <div className="flex items-center gap-4">
                  {detailsData?.logo ? (
                    <Image
                      src={detailsData?.logo}
                      height={48}
                      style={{ objectFit: "contain" }}
                    />
                  ) : null}
                  <div>
                    <h6 className="text-right text-lg font-semibold text-header">
                      {addCommaInNumber(
                        Math.round(balanceCheck?.data?.data?.Amount || 0)
                      )}{" "}
                      BDT
                    </h6>
                    <p className="text-right text-sm font-medium text-display-dark">
                      Balance
                    </p>
                  </div>
                </div>
              </div>
              <div className="space-y-2 mt-4 text-sm font-medium">
                <Item title="Company Name">{detailsData?.company_name}</Item>
                <Item title="Company Email">{detailsData?.business_email}</Item>
                <Item title="Mobile No.">{detailsData?.mobile_number}</Item>
                <Item title="Onboarding Date">
                  {detailsData?.created_at
                    ? formatDate(detailsData?.created_at, "DD MMM YYYY")
                    : "-"}
                </Item>
                <Item
                  title="Location"
                  className="!border-b-0"
                  childrenClass="!text-sm max-w-[300px]"
                >
                  {detailsData?.location}
                </Item>
              </div>

              <div className="bg-white mt-8">
                <CardTitle>Documents</CardTitle>
                <div className="mb-4">
                  <Table
                    dataSource={addKeyInArray(documentList)}
                    columns={columns}
                    pagination={false}
                    className="custom-table border border-extra-light border-b-0 mt-3"
                  />
                </div>

                <CardTitle>Bank Information</CardTitle>
                <div className="space-y-2 my-4 text-sm font-medium mb-4">
                  <Item title="Bank Name">{detailsData?.bank_name}</Item>
                  <Item title="Account Number">
                    {detailsData?.bank_account_number}
                  </Item>
                  <Item title="Branch">{detailsData?.branch_name}</Item>
                </div>

                {/* <CardTitle>Password Change</CardTitle>
                <Item title="Password Reset" className="!border-b-0 !py-4">
                  <Button
                    danger
                    className="flex gap-1 items-center"
                    onClick={() => {
                      handleOpen({ type: "CONFIRM" });
                    }}
                    icon={<KeyIcon />}
                  >
                    Password Reset
                  </Button>
                </Item> */}

                {/* <>
                  {role === "all" ? (
                    <>
                      <CardTitle>PIN Number Set</CardTitle>
                      <Item title="Set PIN" className="!border-b-0 !py-4">
                        <Button
                          danger
                          className="flex gap-1 items-center"
                          onClick={() => {
                            handleOpen({
                              type: "SET_PIN",
                              data: {
                                email: detailsData?.business_email,
                                id: detailsData?.id,
                              },
                            });
                          }}
                          icon={<KeyIcon />}
                        >
                          Set PIN
                        </Button>
                      </Item>
                    </>
                  ) : null}
                </> */}

                <CardTitle
                  rightSide={
                    // <Link
                    //   to={`/admin/businesses/business-list/product-assign/${id}`}
                    // >
                    <Link to={`/admin/users-roll-access/roll-access/add`}>
                      <Button
                        className="outline-button-primary !bg-transparent border-none font-medium !p-0 !h-auto"
                        icon={<AddIcon />}
                      >
                        Add
                      </Button>
                    </Link>
                  }
                >
                  Assign Product
                </CardTitle>

                <div className="mb-4 mt-4">
                  <Table
                    dataSource={addKeyInArray(detailsData?.projectAccesses)}
                    columns={columnAssignProduct}
                    pagination={false}
                    className="custom-table border border-extra-light border-b-0 mt-3"
                  />
                </div>

                <CardTitle
                  rightSide={
                    <Link to={`/admin/users-roll-access/user-list/user-add`}>
                      <Button
                        className="outline-button-primary !bg-transparent border-none font-medium !p-0 !h-auto"
                        icon={<AddIcon />}
                      >
                        Add
                      </Button>
                    </Link>
                  }
                >
                  User Creation
                </CardTitle>
                <div className="mb-4">
                  <Table
                    dataSource={addKeyInArray(detailsData?.users)}
                    columns={columnUserCreation}
                    pagination={false}
                    className="custom-table border border-extra-light border-b-0 mt-3"
                  />
                </div>
              </div>
            </div>
            {/* Right Section */}
            <div className="space-y-4">
              {/* <CardTitle
              // rightSide={
              //   <Button
              //     className="outline-button-primary !bg-transparent border-none font-medium !p-0 !h-auto"
              //     icon={<AddIcon />}
              //   >
              //     Add
              //   </Button>
              // }
              >
                Products
              </CardTitle>

              <div className="py-4 mt-8 flex justify-center">
                <DocumentIcon />
              </div> */}

              {/* <Card className="shadow-sm border border-gray-200 rounded-lg p-0">
             
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center space-x-2">
                    <img src={digiGoLogo} alt="DigiGO" className="w-[100px]" />
                  </div>

                  <span className="status-success">Active</span>
                </div>

          
                <div className="flex justify-between text-gray-500 text-sm text-[#8897AE]">
                  <span>Package</span>
                  <span>Package Expire</span>
                </div>

                <div className="flex justify-between items-center mt-1">
                  <a href="/" className="text-blue-600 font-medium text-base">
                    Package Name
                  </a>
                  <span className="text-[#1A1F36] font-medium text-base">
                    20 Jan, 2026
                  </span>
                </div>

      
                <div className="mt-4 flex justify-end">
                  <Link
                    to="/admin/businesses/business-list/package-details"
                    className="text-orange-500 flex items-center text-sm"
                  >
                    Details <ArrowRightOutlined className="ml-1" />
                  </Link>
                </div>
              </Card>
              <Card className="shadow-sm border border-gray-200 rounded-lg p-0">
       
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center space-x-2">
                    <img src={digiGoLogo} alt="DigiGO" className="w-[100px]" />
                  </div>

                  <span className="status-success">Active</span>
                </div>

      
                <div className="flex justify-between text-gray-500 text-sm text-[#8897AE]">
                  <span>Package</span>
                  <span>Package Expire</span>
                </div>

                <div className="flex justify-between items-center mt-1">
                  <a href="/" className="text-blue-600 font-medium text-base">
                    Package Name
                  </a>
                  <span className="text-[#1A1F36] font-medium text-base">
                    20 Jan, 2026
                  </span>
                </div>

           
                <div className="mt-4 flex justify-end">
                  <a
                    href="/"
                    className="text-orange-500 flex items-center text-sm"
                  >
                    Details <ArrowRightOutlined className="ml-1" />
                  </a>
                </div>
              </Card> */}

              {/* <CardTitle>Logs</CardTitle>

              <div className="py-4 mt-8 flex justify-center">
                <DocumentIcon />
              </div> */}

              {/* <Card>
                <div className="mt-1 space-y-4">
                  {logs.map((log, index) => (
                    <div key={index} className="relative pl-6">
                 
                      {index !== logs.length - 1 && (
                        <div className="absolute left-2 top-5 w-0.5 h-full border-l-2 border-[#AFBACA] border-dashed"></div>
                      )}
                     
                      <div
                        className={`absolute left-0 w-4 h-4 rounded-full ${
                          statusColors[log.status]
                        }`}
                      ></div>

                 
                      <div className="text-[12px] ml-3">
                        <div className="font-semibold text-gray-600 text-[#C19C60]">
                          {log.time} - {log.date}
                        </div>
                        <p className="text-gray-700 text-display-body text-sm mb-1">
                          {log.message}
                        </p>
                        <div className="text-blue-500  flex items-center">
                          <div className="flex items-center gap-2">
                            <span className="text-[#FE7235] w-4 h-4 inline-flex items-center justify-center">
                              <UserIcon />
                            </span>
                            <span className="text-nowrap italic text-[#8897AE]">
                              {log.user} -{" "}
                            </span>
                          </div>
                          <span className="font-medium text-gray-600 ml-1 text-nowrap italic text-[#8897AE]">
                            {log.role}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card> */}
            </div>
          </CustomCard>
        </>
      )}
    </SidebarLayout>
  );
}

const Item = ({
  title,
  children,
  childrenClass,
  className,
}: {
  title: string;
  children: ReactNode;
  className?: string;
  childrenClass?: string;
}) => (
  <div
    className={`flex justify-between items-center border-b border-light pb-2 ${className}`}
  >
    <h6 className="text-display-dark font-medium">{title}</h6>
    <div
      className={`text-display-body font-medium text-base text-right ${childrenClass}`}
    >
      {children}
    </div>
  </div>
);
